import { useState } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useChainId } from 'wagmi'
import { keccak256, encodePacked } from 'viem'
import { getNFTContract, parseContractError } from '@/config/contract'
import { useContractInfo } from './useContractInfo'
import { defaultChainId, isSupportedNetwork } from '@/config/wagmi'
import axios from 'axios'
import toast from 'react-hot-toast'
import { MerkleTree } from 'merkletreejs'

const API_URL = import.meta.env.VITE_API_URL || ''

// Helper function to convert hex string to Uint8Array (browser-compatible)
const hexToUint8Array = (hex: string): Uint8Array => {
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex
  const bytes = new Uint8Array(cleanHex.length / 2)
  for (let i = 0; i < cleanHex.length; i += 2) {
    bytes[i / 2] = parseInt(cleanHex.substr(i, 2), 16)
  }
  return bytes
}

// Helper function to convert Uint8Array to hex string
const uint8ArrayToHex = (bytes: Uint8Array): string => {
  return '0x' + Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export interface MintState {
  isLoading: boolean
  isConfirming: boolean
  isSuccess: boolean
  error: string | null
  transactionHash: string | null
}

export const useMint = () => {
  const { address, isConnected } = useAccount()
  const chainIdFromWallet = useChainId()

  // For write actions we still require a connected wallet, but
  // we choose the chainId the same way as in useContractInfo to
  // avoid accidentally pointing to 31337 in production.
  const chainId = isConnected && isSupportedNetwork(chainIdFromWallet || 0)
    ? (chainIdFromWallet as number)
    : defaultChainId
  const contract = getNFTContract(chainId)
  const { data: contractInfo } = useContractInfo()
  const { writeContract, data: hash, reset } = useWriteContract()

  // Explicit gas limit to avoid "gas limit too high" errors and crazy estimates
  // Hardhat local: high limit; public networks (Sepolia, BSC): conservative 800k for mint
  const mintGasLimit: bigint = chainId === 31337 ? 5_000_000n : 800_000n
  
  const [state, setState] = useState<MintState>({
    isLoading: false,
    isConfirming: false,
    isSuccess: false,
    error: null,
    transactionHash: null,
  })

  // Watch for transaction confirmation
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  /**
   * Hash address the same way as Solidity: keccak256(abi.encodePacked(address))
   */
  const hashAddress = (address: string): `0x${string}` => {
    const normalized = address.toLowerCase()
    return keccak256(encodePacked(['address'], [normalized as `0x${string}`]))
  }

  /**
   * Get Merkle Proof from backend or localStorage fallback
   */
  const getMerkleProof = async (address: string): Promise<string[]> => {
    // Try backend first
    if (API_URL) {
      try {
        const response = await axios.get(`${API_URL}/api/whitelist-proof/${address}`, {
          timeout: 5000
        })
        if (response.data.proof && response.data.proof.length > 0) {
          return response.data.proof
        }
      } catch (error) {
        console.warn('Backend not available, trying localStorage fallback...', error)
      }
    }

    // Fallback: Try to get proof from localStorage merkle tree data
    try {
      const savedData = localStorage.getItem('merkleTreeData')
      if (savedData) {
        const data = JSON.parse(savedData)
        if (data.addresses && data.merkleRoot && data.proofs) {
          const normalizedAddress = address.toLowerCase()
          if (data.proofs[normalizedAddress]) {
            console.log('✅ Using merkle proof from localStorage')
            return data.proofs[normalizedAddress]
          }
        }
        
        // If proofs not saved, regenerate tree and get proof
        if (data.addresses && data.addresses.length > 0) {
          const normalizedAddress = address.toLowerCase()
          if (data.addresses.includes(normalizedAddress)) {
            // Regenerate tree from saved addresses
            const leaves = data.addresses.map((addr: string) => hashAddress(addr))
            const tree = new MerkleTree(
              leaves.map((leaf: string) => hexToUint8Array(leaf)) as any[],
              (data: Uint8Array) => {
                const hex = uint8ArrayToHex(data)
                const hashed = keccak256(hex as `0x${string}`)
                return hexToUint8Array(hashed)
              },
              { sortPairs: true }
            ) as any
            
            const leaf = hashAddress(normalizedAddress)
            const leafBytes = hexToUint8Array(leaf)
            const proof = (tree as any).getHexProof(leafBytes as any)
            
            console.log('✅ Generated merkle proof from localStorage addresses')
            return proof
          }
        }
      }
    } catch (error) {
      console.error('Failed to get merkle proof from localStorage:', error)
    }

    console.warn('⚠️ No merkle proof found. Make sure addresses are added via MerkleTreeManager in Admin Debug.')
    return []
  }

  /**
   * Main mint function
   * Handles both whitelist and public minting
   */
  const mint = async (quantity: number) => {
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }

    if (!contractInfo) {
      toast.error('Contract information not loaded')
      return
    }

    // Reset previous state
    reset()
    setState({
      isLoading: true,
      isConfirming: false,
      isSuccess: false,
      error: null,
      transactionHash: null,
    })

    try {
      // Validate quantity
      if (quantity <= 0) {
        throw new Error('Quantity must be greater than 0')
      }

      if (quantity > Number(contractInfo.maxPerTx)) {
        throw new Error(`Maximum ${contractInfo.maxPerTx} NFTs per transaction`)
      }

      // Check if paused
      if (contractInfo.isPaused) {
        throw new Error('Minting is currently paused')
      }

      // Check supply
      const remaining = Number(contractInfo.maxSupply) - Number(contractInfo.totalMinted)
      if (quantity > remaining) {
        throw new Error(`Only ${remaining} NFTs remaining`)
      }

      // Calculate total cost
      const currentPrice = BigInt(contractInfo.currentPrice)
      const totalCost = currentPrice * BigInt(quantity)

      // Get merkle proof if whitelist is active
      let merkleProof: string[] = []
      if (contractInfo.whitelistActive) {
        toast.loading('Checking whitelist status...')
        merkleProof = await getMerkleProof(address)
        
        if (merkleProof.length === 0) {
          throw new Error('You are not on the whitelist')
        }
        
        toast.dismiss()
        toast.success('Whitelist verified!')
      }

      // Show transaction prompt
      toast.loading('Please confirm the transaction in your wallet...', {
        id: 'mint-prompt',
      })

      // Execute mint transaction
      writeContract(
        {
          ...contract,
          functionName: 'mint',
          args: [BigInt(quantity), merkleProof as `0x${string}`[]],
          value: totalCost,
          gas: mintGasLimit,
        },
        {
          onError: (error) => {
            toast.dismiss('mint-prompt')
            const errorMessage = parseContractError(error as Error)
            setState((prev) => ({
              ...prev,
              isLoading: false,
              error: errorMessage,
            }))
            toast.error(errorMessage)
          },
          onSuccess: () => {
            toast.dismiss('mint-prompt')
            toast.loading('Transaction submitted! Waiting for confirmation...', {
              id: 'mint-confirming',
            })
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isConfirming: true,
            }))
          },
        }
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to mint'
      setState({
        isLoading: false,
        isConfirming: false,
        isSuccess: false,
        error: errorMessage,
        transactionHash: null,
      })
      toast.error(errorMessage)
    }
  }

  // Update state when transaction is confirmed
  if (isSuccess && !state.isSuccess) {
    toast.dismiss('mint-confirming')
    toast.success('NFT minted successfully! 🎉')
    setState((prev) => ({
      ...prev,
      isConfirming: false,
      isSuccess: true,
      transactionHash: hash || null,
    }))
  }

  // Update confirming state
  if (isConfirming && !state.isConfirming && state.isLoading) {
    setState((prev) => ({
      ...prev,
      isConfirming: true,
      isLoading: false,
    }))
  }

  return {
    mint,
    ...state,
    reset: () => {
      reset()
      setState({
        isLoading: false,
        isConfirming: false,
        isSuccess: false,
        error: null,
        transactionHash: null,
      })
    },
  }
}
